import { Box, IconButton, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { calculateEmployeeCoverage, calculateLearningProgress } from '@sharedInterfaces/globalHelper';
import type { IEmployeeSuggestedCompetence } from '@sharedInterfaces/IEmployee';
import type { IEmployeeSettingsCompetenceSettingsWantedCompetence } from '@sharedInterfaces/IEmployeeSettings';
import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import deleteWantedCompetence from '@src/APIs/graphQl/Employee/WantedCompetences/deleteWantedCompetenceState';
import updateWantedCompetenceState from '@src/APIs/graphQl/Employee/WantedCompetences/updateWantedCompetenceState';
import Button from '@src/App/Components/Buttons/Button/Button';
import { useConfirmDeleteDialog } from '@src/App/Components/Dialogs/ConfirmDelete/confirmDelete';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { EmployeeCoverage } from '@src/App/Components/General/EmployeeSuggestion/EmployeeSuggestions/EmployeeCoverage';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { ConfigureWantedCometenceDialog } from '@src/App/Components/Goals/SuggestedCompetences/ConfigureWantedCometenceDialog';
import ItemBadge from '@src/App/Components/Items/ItemBadge/ItemBadge';
import { setWantedCompetences } from '@store/reducer/employeeSettingsReducer';
import type { AppState } from '@store/store';
import { Calendar, CheckCircle, Settings2, Target, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fireworkSalvo } from './fireworkSalvo';

/**
 * Function to generate EDPWantedCompetenceCard component.
 *
 * @param wantedCompetence - The wanted competence settings for an employee.
 * @returns The JSX element for EDPWantedCompetenceCard.
 */
export function EDPWantedCompetenceCard({
    wantedCompetence,
}: {
    wantedCompetence: IEmployeeSettingsCompetenceSettingsWantedCompetence;
}) {
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const allCompetence = allCompetences.find((a) => a.id === wantedCompetence.id);
    const empSkills = useSelector((state: AppState) => state.employee.skills);
    const empCertificates = useSelector((state: AppState) => state.employee.certificates);
    const competenceLevelSkills =
        allCompetence && allCompetence.levels[wantedCompetence.level - 1]
            ? allCompetence.levels[wantedCompetence.level - 1].skills
            : [];
    const competenceLevelCertificates =
        allCompetence && allCompetence.levels[wantedCompetence.level - 1]
            ? allCompetence.levels[wantedCompetence.level - 1].certificates
            : [];
    const [coverage, setCoverage] = useState<IEmployeeCoverage | null>(
        allCompetence
            ? calculateEmployeeCoverage(
                  competenceLevelSkills,
                  empSkills,
                  competenceLevelCertificates,
                  empCertificates,
                  [],
                  [],
              )
            : null,
    );
    const [score, setScore] = useState<number>(
        allCompetence && coverage
            ? calculateLearningProgress(empSkills, wantedCompetence.initialCompareState, competenceLevelSkills)
            : 0,
    );

    useEffect(() => {
        if (!allCompetence) return;
        setCoverage(
            calculateEmployeeCoverage(
                competenceLevelSkills,
                empSkills,
                competenceLevelCertificates,
                empCertificates,
                [],
                [],
            ),
        );
    }, [
        empSkills.length,
        empSkills,
        allCompetence,
        wantedCompetence,
        competenceLevelSkills,
        competenceLevelSkills.length,
        empCertificates,
        competenceLevelCertificates,
        competenceLevelCertificates.length,
    ]);

    useEffect(() => {
        if (!coverage) return;
        setScore(calculateLearningProgress(empSkills, wantedCompetence.initialCompareState, competenceLevelSkills));
    }, [empSkills.length, empSkills, coverage, competenceLevelSkills, competenceLevelSkills.length]);

    if (
        !wantedCompetence ||
        !wantedCompetence.id ||
        wantedCompetence.level === undefined ||
        !allCompetence ||
        !coverage
    )
        return null;

    return (
        <CompetenceGoalCard
            competence={{
                id: allCompetence.id,
                title: allCompetence.title,
                description: '',
                level: wantedCompetence.level,
                employee: coverage,
            }}
            wantedCompetence={wantedCompetence}
            score={score}
            update={(): void => {
                setCoverage(
                    calculateEmployeeCoverage(
                        allCompetence.levels[wantedCompetence.level - 1].skills,
                        empSkills,
                        allCompetence.levels[wantedCompetence.level - 1].certificates,
                        empCertificates,
                        [],
                        [],
                    ),
                );
                setScore(
                    calculateLearningProgress(empSkills, wantedCompetence.initialCompareState, competenceLevelSkills),
                );
            }}
        />
    );
}
/**
 * Generate a suggested competence element.
 * @param {competence} - The competence object.
 * @returns {JSX.Element} - The suggested competence element.
 */
export function CompetenceGoalCard({
    competence,
    score,
    wantedCompetence,
    update,
}: {
    competence: IEmployeeSuggestedCompetence;
    score: number;
    wantedCompetence: IEmployeeSettingsCompetenceSettingsWantedCompetence;
    update: () => void;
}) {
    const theme = useTheme();
    const confirmDelete = useConfirmDeleteDialog();
    const { openDialog } = useDialog();
    const showErrorDialog = useErrorDialog();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const wantedCompetences = useSelector(
        (state: AppState) => state.employeeSettings.competenceSettings.wantedCompetences,
    );
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const offline = useSelector((state: AppState) => state.client.offline);
    const originalScoreRef = useRef(score);
    const allCompetence = allCompetences.find((a) => a.id === competence.id);

    useEffect(() => {
        originalScoreRef.current = score;
    }, [competence.id]);

    // useEffect(() =>
    // {

    //     if (score >= 1 && originalScoreRef.current < 1)
    //     {
    //         // fireworkSalvo();
    //     }
    // }, [score, originalScoreRef]);

    const onConfirm = () => {
        fireworkSalvo();
        updateWantedCompetenceState(competence.id)
            .then(() => {
                const newWantedCompetences = [...wantedCompetences];
                const wantedCompetence = newWantedCompetences.find((wc) => wc.id === competence.id);
                if (wantedCompetence) {
                    wantedCompetence.state = 'done';
                    dispatch(setWantedCompetences(newWantedCompetences));
                }
            })
            .catch(showErrorDialog);
    };

    const onDelete = () => {
        confirmDelete(`${langStrings.competenceGoal} - ${competence.title}`).then(() => {
            deleteWantedCompetence(competence.id)
                .then(() => {
                    dispatch(setWantedCompetences(wantedCompetences.filter((wc) => wc.id !== competence.id)));
                })
                .catch(showErrorDialog);
        });
    };
    const onConfigure = () => {
        openDialog(
            <ConfigureWantedCometenceDialog
                id={'ConfigureWantedCometenceDialog'}
                competence={competence}
                untilDate={wantedCompetence.untilDate}
                dontFilterLevel={true}
                resolve={() => {
                    update();
                }}
            />,
        );
    };
    if (!allCompetence) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Target size={20} color={theme.palette.primary.main} />
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {competence.title}
                </Typography>
                <ItemBadge
                    value={allCompetence.levels[competence.level - 1].title}
                    title={allCompetence.levels[competence.level - 1].title}
                    level={competence.level}
                    size="small"
                />
            </Box>

            <Stack spacing={2}>
                {/* Date Cards */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Calendar size={16} color={theme.palette.info.main} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                {langStrings.oftenUsed.createdAt}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {wantedCompetence.createdAt.toLocaleDateString()}
                        </Typography>
                    </Box>

                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Target size={16} color={theme.palette.warning.main} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                                {langStrings.untilDate}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {wantedCompetence.untilDate.toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>

                {/* Progress */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {langStrings.learnProgress}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {Math.round(score * 100)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={score * 100}
                        sx={{
                            width: '100%',
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: score < 0.95 ? theme.palette.primary.main : theme.palette.success.main,
                                borderRadius: 4,
                            },
                        }}
                    />
                </Box>

                {/* Description */}
                {competence.description && competence.description !== '' && (
                    <FormatedTextInput value={competence.description} readonly={true} />
                )}

                {/* Coverage */}
                <EmployeeCoverage expended suggestedEmployee={competence.employee} hideName hideAvailability />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                    <Button
                        helpText={langStrings.oftenUsed.delete}
                        color={'red'}
                        disabled={offline}
                        text={langStrings.oftenUsed.delete}
                        icon={<Trash2 />}
                        onClick={onDelete}
                    />
                    <Button
                        text={langStrings.oftenUsed.edit}
                        disabled={offline}
                        icon={<Settings2 />}
                        onClick={onConfigure}
                    />
                    {score >= 1 && (
                        <Button
                            text={langStrings.confirmHideGoal}
                            disabled={offline}
                            icon={<CheckCircle />}
                            color={'green'}
                            onClick={onConfirm}
                        />
                    )}
                </Box>
            </Stack>
        </Paper>
    );
}
